"use client";

import React, { useState, useEffect } from "react";
import Accordion from "../Accordions/Accordion";
import TabbedContentDisplay from "../ContentDisplay/TabbedContentDisplay";
import styles from "./SequenceDisplay.module.css";

/**
 * SequenceDisplay Component
 *
 * This component supports two modes:
 *
 * 1. Single Item Detail Mode:
 *    If an `item` prop is provided, it displays that one content item directly.
 *    This is the correct mode for the item accordions inside /pages/Sequences.jsx.
 *
 * 2. Legacy Full Sequence Mode:
 *    If no `item` prop is provided, it falls back to the older behavior:
 *    fetch all items for a sequence and render them as an accordion stack.
 *
 * @param {number} sequenceId - The ID of the sequence to display
 * @param {Object} item - Optional single sequence/content item to display
 */
const SequenceDisplay = ({ sequenceId, item = null }) => {
  const [sequenceItems, setSequenceItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Forces re-render of Accordion in legacy full-sequence mode.
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    if (!item && sequenceId) {
      fetchSequenceItems(sequenceId);
    }
  }, [item, sequenceId]);

  const openContentAdmin = (contentItem) => {
    if (!contentItem?.content_id) {
      alert("This item does not have a content_id available.");
      return;
    }

    window.open(
      `/ContentAdmin?contentId=${contentItem.content_id}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  /**
   * Single Item Detail Mode
   *
   * This prevents the nested accordion problem.
   * When /pages/Sequences.jsx opens one sequence item, it passes that specific item here.
   */
  if (item) {
    return (
      <div className={styles.sequenceDisplay}>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "8px",
          }}
        >
          <button
            type="button"
            onClick={() => openContentAdmin(item)}
            style={{
              padding: "4px 8px",
              border: "1px solid #444",
              borderRadius: "4px",
              background: "#222",
              color: "#eee",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Open in Content Admin
          </button>
        </div>

        <TabbedContentDisplay content={item} />
      </div>
    );
  }

  /**
   * Fetch items for this sequence from /api/sequences/viewSequence?sequenceId=...
   *
   * This is preserved for legacy usage where SequenceDisplay is used to render
   * an entire sequence by itself.
   */
  const fetchSequenceItems = async (seqId) => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`/api/sequences/viewSequence?sequenceId=${seqId}`);

      if (!res.ok) {
        throw new Error("Failed to fetch sequence items");
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Failed to fetch sequence items");
      }

      setSequenceItems(data.contentItems || []);
    } catch (err) {
      console.error("[SequenceDisplay] fetchSequenceItems -> error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reorder items with arrow-based approach using Accordion's onLocalReorder.
   */
  const handleArrowReorder = (newLocalItems) => {
    const updated = newLocalItems.map((accItem) =>
      sequenceItems.find((si) => si.sequence_item_id === accItem.id) || accItem
    );

    setSequenceItems(updated);
    updateSequenceItemOrder(updated);
  };

  /**
   * Save new item positions by calling /api/sequences/updateItemOrder.
   */
  const updateSequenceItemOrder = async (updatedItems) => {
    const orderPayload = updatedItems.map((itm, idx) => ({
      id: itm.sequence_item_id,
      position: idx,
    }));

    console.log("[SequenceDisplay] updateSequenceItemOrder ->", orderPayload);

    try {
      const res = await fetch("/api/sequences/updateItemOrder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sequenceId, order: orderPayload }),
      });

      if (!res.ok) {
        throw new Error("Failed to update item order.");
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Update item order request failed.");
      }

      console.log(
        `[SequenceDisplay] item order for sequence ${sequenceId} updated successfully.`
      );

      setResetKey((prev) => prev + 1);
    } catch (err) {
      console.error("[SequenceDisplay] Error updating item order:", err);
      alert(err.message);
    }
  };

  /**
   * Remove one item from the sequence by calling /api/sequences/removeItemFromSequence.
   */
  const removeItem = async (sequenceItemId) => {
    if (!window.confirm("Are you sure you want to remove this item?")) return;

    console.log(
      `[SequenceDisplay] removeItem -> sequenceId=${sequenceId}, sequence_item_id=${sequenceItemId}`
    );

    try {
      const res = await fetch("/api/sequences/removeItemFromSequence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sequenceId, sequenceItemId }),
      });

      if (!res.ok) {
        throw new Error("Failed to remove item from sequence.");
      }

      const data = await res.json();

      if (!data.success) {
        throw new Error(data.message || "Remove request failed.");
      }

      console.log(
        `Item ${sequenceItemId} removed from sequence ${sequenceId} successfully.`
      );

      setSequenceItems((prev) =>
        prev.filter((itm) => itm.sequence_item_id !== sequenceItemId)
      );
    } catch (err) {
      console.error("[SequenceDisplay] Error removing item:", err);
      alert(err.message);
    }
  };

  /**
   * Build Accordion items from our sequenceItems array.
   */
  const buildAccordionItems = () => {
    return sequenceItems.map((itm) => ({
      header: itm.name || "Unnamed",
      content: <TabbedContentDisplay content={itm} />,
      id: itm.sequence_item_id,
    }));
  };

  /**
   * Called by the trash icon in arrow-based mode if onRemoveItem is provided.
   */
  const handleRemoveItem = (accItem) => {
    const found = sequenceItems.find((si) => si.sequence_item_id === accItem.id);

    if (found) {
      removeItem(found.sequence_item_id);
    }
  };

  if (!sequenceId) {
    return <p style={{ color: "red" }}>No sequenceId provided.</p>;
  }

  if (loading) {
    return <p>Loading items for sequence {sequenceId}...</p>;
  }

  if (error) {
    return <p style={{ color: "red" }}>Error: {error}</p>;
  }

  if (sequenceItems.length === 0) {
    return <p>No items in this sequence.</p>;
  }

  return (
    <div className={styles.sequenceDisplay}>
      {/* Legacy full-sequence accordion mode */}
      <Accordion
        key={`sequence-accordion-${resetKey}`}
        items={buildAccordionItems()}
        onLocalReorder={handleArrowReorder}
        onRemoveItem={handleRemoveItem}
        collapsible={true}
      />
    </div>
  );
};

export default SequenceDisplay;
